import { useEffect, useMemo, useState } from 'react'
import { handleError } from 'Helpers'
import {
  StationAssignment,
  StationStaffDirectoryUser,
  StationStaffRole,
  stationStaffService,
} from 'BoostXpressAdmin/Services/stationStaffService'

export type StationStaffOption = {
  value: string
  label: string
  role: StationStaffRole
}

const formatStaffLabel = (user: {
  resourceId: string
  firstName: string
  lastName: string
  email: string
}) => {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  return name && user.email ? `${name} (${user.email})` : name || user.email || user.resourceId
}

const directoryUserToOption = (user: StationStaffDirectoryUser): StationStaffOption => ({
  value: user.resourceId,
  label: formatStaffLabel(user),
  role: user.accountType,
})

const assignmentToOption = (assignment: StationAssignment): StationStaffOption => ({
  value: assignment.userResourceId,
  label: formatStaffLabel({
    resourceId: assignment.userResourceId,
    firstName: assignment.userFirstName,
    lastName: assignment.userLastName,
    email: assignment.userEmail,
  }),
  role: assignment.role,
})

export const mergeAssignmentStaffOptions = (
  directoryOptions: StationStaffOption[],
  assignments: StationAssignment[],
) => {
  const optionsByUser = new Map(directoryOptions.map((option) => [option.value, option]))
  assignments.forEach((assignment) => {
    if (!optionsByUser.has(assignment.userResourceId)) {
      optionsByUser.set(assignment.userResourceId, assignmentToOption(assignment))
    }
  })
  return Array.from(optionsByUser.values())
}

export const filterStaffOptions = (
  options: StationStaffOption[],
  role: StationStaffRole,
  excludedUserId: string,
  selectedUserId: string,
) =>
  options.filter(
    (option) =>
      option.role === role && (option.value !== excludedUserId || option.value === selectedUserId),
  )

export const useStationStaffOptions = () => {
  const [directoryUsers, setDirectoryUsers] = useState<StationStaffDirectoryUser[]>([])
  const [isLoadingStaff, setIsLoadingStaff] = useState(true)

  useEffect(() => {
    let cancelled = false

    const loadStaff = async () => {
      try {
        setIsLoadingStaff(true)
        const users = await stationStaffService.listAllDirectoryUsers()
        if (!cancelled) setDirectoryUsers(users)
      } catch {
        if (cancelled) return
        setDirectoryUsers([])
        handleError({ response: { data: { error: 'Failed to load available station staff.' } } })
      } finally {
        if (!cancelled) setIsLoadingStaff(false)
      }
    }

    loadStaff()
    return () => {
      cancelled = true
    }
  }, [])

  const staffOptions = useMemo(
    () => directoryUsers.filter((user) => !user.disabledAt).map(directoryUserToOption),
    [directoryUsers],
  )
  return { staffOptions, isLoadingStaff }
}
